import { zodResolver } from '@hookform/resolvers/zod'
import { deepEq, isPresent } from 'lib/utils'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useForm,
  UseFormProps,
  ValidationMode,
} from 'react-hook-form'
import { z } from 'zod'

export type OnSubmitResult = {
  FORM_ERROR?: string
  [k: string]: any
}

export let FORM_ERROR = 'FORM_ERROR'

let useBaseForm = <S extends z.ZodType<any, any>>(
  onSubmit?: (values: z.infer<S>) => Promise<void | OnSubmitResult>,
  schema?: S,
  initialValues?: UseFormProps<z.input<S>>['defaultValues'],
  mode: keyof ValidationMode = 'onTouched',
  reValidateMode: UseFormProps['reValidateMode'] = 'onChange'
) => {
  let memoizedSchema = useMemo(() => schema, [schema])

  let ctx = useForm<z.input<S>>({
    reValidateMode,
    mode,
    resolver: isPresent(memoizedSchema)
      ? zodResolver(memoizedSchema)
      : undefined,
    defaultValues: initialValues,
  })

  let [formError, setFormError] = useState<string | null>(null)
  let formValues = useRef(null)

  let _handleSubmit = useCallback(
    async (data: z.infer<S>) => {
      setFormError(null)
      formValues.current = ctx.getValues()
      try {
        await onSubmit?.(data)
      } catch (error: any) {
        setFormError(error.error)
        formValues.current = ctx.getValues()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSubmit]
  )

  let currentFormValues = ctx.watch()

  useEffect(() => {
    if (!deepEq(formValues.current, currentFormValues)) {
      setFormError(null)
      formValues.current = ctx.getValues()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFormValues])

  return {
    formCtx: { ...ctx, formError, defaultValues: initialValues },
    formError,
    formIsSubmitting: ctx.formState.isSubmitting,
    submitForm: ctx.handleSubmit(_handleSubmit),
  }
}

export default useBaseForm
